import React, { useEffect, useMemo, useState } from 'react';
import { LifeBuoy, AlertTriangle, Plus, Phone, MessageCircle, Eye } from 'lucide-react';
import { ref, query, orderByChild, equalTo, onValue } from 'firebase/database';
import { db } from '../../../integrations/firebase';
import { recordRead, approxBytes } from '../../../integrations/rtdbMeter';
import { useChat } from '../../chat/hooks/useChat';
import { STATUS_LABELS } from '../constants';
import { summarizeCustomerComplaints, customerComplaintWarnings } from '../logic/customerComplaints';
import CreateTicketModal from './CreateTicketModal';
import TicketDetailDrawer from './TicketDetailDrawer';
import QuickCallButton from '../../customers/ui/QuickCallButton';

const norm = (m) => String(m || '').replace(/\D/g, '').slice(-10);
const fmt = (ts) => ts ? new Date(ts).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';

const Stat = ({ label, value, tone = 'slate' }) => {
    const tones = {
        slate: 'text-slate-700', purple: 'text-purple-700', blue: 'text-blue-700',
        green: 'text-green-700', amber: 'text-amber-700', red: 'text-red-700',
    };
    return (
        <div className="bg-slate-50 rounded-lg px-3 py-2 text-center">
            <div className={`text-lg font-bold ${tones[tone]}`}>{value}</div>
            <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
        </div>
    );
};

const priorityCls = {
    urgent: 'bg-red-100 text-red-700', high: 'bg-amber-100 text-amber-700',
    medium: 'bg-blue-100 text-blue-700', low: 'bg-slate-100 text-slate-600',
};

/**
 * Read-only complaint history for one customer, sourced from the live ticket
 * store filtered by mobile. Links into the existing ticket module — it does NOT
 * re-implement ticketing.
 */
const CustomerComplaintsPanel = ({ mobile, customerName, currentUser: currentUserProp }) => {
    const { currentUser: chatUser } = useChat();
    const currentUser = currentUserProp || chatUser;
    const [showCreate, setShowCreate] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [myTickets, setMyTickets] = useState([]);

    const m = norm(mobile);

    // ONE customer's tickets, over the `mobile` index.
    //
    // This panel used to subscribe to the whole `tickets` tree and filter it in
    // the browser, so opening any customer profile downloaded every ticket in
    // the business to show the handful belonging to that one customer.
    useEffect(() => {
        if (!m) { setMyTickets([]); return; }
        const q = query(ref(db, 'tickets'), orderByChild('mobile'), equalTo(m));
        const unsub = onValue(q, (snap) => {
            try { recordRead(`/tickets?mobile=${m}`, approxBytes(snap.val())); } catch { /* ignore */ }
            const out = [];
            snap.forEach((c) => { out.push({ id: c.key, ...c.val() }); });
            setMyTickets(out);
        }, () => setMyTickets([]));
        return () => unsub();
    }, [m]);
    const counts = useMemo(() => summarizeCustomerComplaints(myTickets), [myTickets]);
    const warnings = useMemo(() => customerComplaintWarnings(myTickets), [myTickets]);
    const selected = myTickets.find(t => t.id === selectedId) || null;

    return (
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
            <div className="flex items-center justify-between mb-4 pb-2 border-b border-slate-50">
                <div className="flex items-center gap-2">
                    <LifeBuoy size={18} className="text-purple-600" />
                    <h3 className="font-bold text-slate-800">Complaints &amp; Tickets</h3>
                </div>
                <div className="flex items-center gap-2">
                    {mobile && <QuickCallButton mobile={mobile} customerName={customerName} ticketId={selected?.id || (myTickets[0] && myTickets[0].id) || ''} orderId={selected?.orderId || ''} sourceModule="complaints" callType="complaint_followup" />}
                    {mobile && <a target="_blank" rel="noreferrer" href={`https://wa.me/91${m}`} className="p-1.5 rounded hover:bg-emerald-50 text-emerald-600" title="WhatsApp"><MessageCircle size={16} /></a>}
                    <button onClick={() => setShowCreate(true)} className="flex items-center gap-1 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 px-3 py-1.5 rounded-lg">
                        <Plus size={15} /> New Ticket
                    </button>
                </div>
            </div>

            {myTickets.length === 0 ? (
                <p className="text-sm text-slate-400 py-4 text-center">No complaints recorded for this customer.</p>
            ) : (
                <>
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-4">
                        <Stat label="Total" value={counts.total} tone="purple" />
                        <Stat label="Open" value={counts.open} tone="blue" />
                        <Stat label="Closed" value={counts.closed} tone="green" />
                        <Stat label="Doctor" value={counts.doctor} tone="amber" />
                        <Stat label="Delivery" value={counts.delivery} tone="slate" />
                        <Stat label="Refund/Repl." value={counts.refundReplacement} tone="red" />
                    </div>

                    {warnings.length > 0 && (
                        <div className="mb-4 space-y-1">
                            {warnings.map((w, i) => (
                                <div key={i} className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-2 py-1.5">
                                    <AlertTriangle size={14} className="shrink-0" /> {w}
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="overflow-x-auto border border-slate-100 rounded-lg">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase">
                                <tr>
                                    {['Ticket', 'Date', 'Issue', 'Product', 'Order', 'Status', 'Priority', 'Dept', 'Updated', ''].map(h =>
                                        <th key={h} className="text-left px-2 py-2 font-semibold whitespace-nowrap">{h}</th>)}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {myTickets.map(t => (
                                    <tr key={t.id} className="hover:bg-slate-50">
                                        <td className="px-2 py-2 font-mono text-xs font-bold text-purple-700">{t.ticketNumber}</td>
                                        <td className="px-2 py-2 text-slate-500">{fmt(t.createdAt)}</td>
                                        <td className="px-2 py-2">{t.issueType}</td>
                                        <td className="px-2 py-2 text-slate-500">{(t.productNames || []).join(', ') || '—'}</td>
                                        <td className="px-2 py-2 text-slate-500">{t.orderId || '—'}</td>
                                        <td className="px-2 py-2 text-xs">{STATUS_LABELS[t.status] || t.status}</td>
                                        <td className="px-2 py-2"><span className={`px-2 py-0.5 rounded text-xs font-semibold ${priorityCls[t.priority] || ''}`}>{t.priority}</span></td>
                                        <td className="px-2 py-2 capitalize text-slate-500">{t.assignedDepartment}</td>
                                        <td className="px-2 py-2 text-slate-400 text-xs">{fmt(t.updatedAt)}</td>
                                        <td className="px-2 py-2">
                                            <button onClick={() => setSelectedId(t.id)} title="View ticket" className="p-1.5 hover:bg-purple-100 rounded text-purple-600"><Eye size={15} /></button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {showCreate && (
                <CreateTicketModal
                    isOpen={showCreate}
                    onClose={() => setShowCreate(false)}
                    currentUser={currentUser}
                    existingTickets={myTickets}
                    prefill={{ mobile: mobile || '', customerName: customerName || '' }}
                    onCreated={(t) => setSelectedId(t.id)}
                />
            )}
            {selected && <TicketDetailDrawer ticket={selected} currentUser={currentUser} onClose={() => setSelectedId(null)} />}
        </div>
    );
};

export default CustomerComplaintsPanel;
